import { format } from 'date-fns';
import { RRule } from 'rrule';
import { compact, isNullOrUndefined, recordOrEmpty } from '../../../lib/utils';
import {
  asDateNumber,
  shiftDateTimeFormat,
} from '../../../lib/utils/date-time';
import { removeApproximateFromRecurrentPatternText } from './RRule';

import type { FilledShift, SimpleUser } from '../types';
import type {
  CreateOrUpdateShift,
  NoteToCreate,
  ShiftExceptionUpdate,
  ShiftModifiers,
} from './shift-service';
import type { NoteShiftChange } from '../../Notes/types';
import type { NoteContent } from '../../../hooks/types';

/**
 * The default string shown when a field in a changelog is empty.
 */
const defaultEmptyNoteField = '';

/**
 * Determines whether the given shift is derived, and if so, returns the date
 * that the derived shift is held.
 */
export const dateIfDerived = (
  shift: FilledShift | undefined
): number | undefined =>
  shift?.isDerived === true ? asDateNumber(shift.startTime) : undefined;

/**
 * Used to build a notes object for a shift.
 */
const buildShiftNote = (
  noteContent: string | undefined,
  shiftDate: string,
  changes: readonly NoteShiftChange[] | undefined
): NoteContent | undefined => {
  if (
    noteContent === undefined &&
    (changes === undefined || changes.length === 0)
  ) {
    return undefined;
  }

  return {
    date: shiftDate,
    ...recordOrEmpty('content', noteContent),
    ...recordOrEmpty(
      'metadata',
      changes === undefined || changes.length === 0
        ? undefined
        : { shift: { changes } }
    ),
  };
};

/**
 * Compares selected fields and returns an array of the fields that have been changed,
 * and their changes.
 */
const changeLog = <T extends CreateOrUpdateShift | ShiftExceptionUpdate>(
  currentShift: FilledShift,
  newOrUpdatedUser: SimpleUser | undefined,
  newOrUpdatedShift: T
) => {
  const user: NoteShiftChange = {
    field: 'Personnel',
    from: {
      id: currentShift.user?.userId ?? defaultEmptyNoteField,
      label: fullName(currentShift.user),
    },
    to: {
      id: newOrUpdatedUser?.userId ?? defaultEmptyNoteField,
      label: fullName(newOrUpdatedUser),
    },
  };

  const status: NoteShiftChange = {
    field: 'Status',
    from: currentShift.status,
    to: newOrUpdatedShift.shift_status,
  };

  // Recurrence is undefined for shift exceptions, but defined otherwise even for non-recurring shifts
  const recurrence =
    'is_recurring' in newOrUpdatedShift
      ? {
          is_recurring: newOrUpdatedShift.is_recurring,
          recurrence_end_date: newOrUpdatedShift.recurrence_end_date,
          recurrence_pattern: newOrUpdatedShift.recurrence_pattern,
        }
      : undefined;

  // If the action is to delete future recurrences, don't show the start and end time changes.
  const deleteFutureShifts =
    !isNullOrUndefined(recurrence) &&
    !isNullOrUndefined(recurrence.recurrence_end_date) &&
    recurrence.recurrence_end_date < new Date(currentShift.startTime);

  const times: readonly NoteShiftChange[] = deleteFutureShifts
    ? []
    : [
        {
          field: 'Start Time',
          from: formatDate(new Date(currentShift.startTime)),
          to: formatDate(newOrUpdatedShift.start_time),
        },
        {
          field: 'End Time',
          from: formatDate(new Date(currentShift.endTime)),
          to: formatDate(newOrUpdatedShift.end_time),
        },
      ];

  const recurrencePattern: readonly NoteShiftChange[] =
    recurrence === undefined
      ? [
          {
            field: 'Exception Date',
            from: defaultEmptyNoteField,
            to: format(
              newOrUpdatedShift.start_time ?? new Date(currentShift.startTime),
              'PPP'
            ),
          },
        ]
      : currentShift.isRecurring || recurrence.is_recurring
        ? ([
            {
              field: 'Recurrence Pattern',
              from: formatRecurrencePattern(currentShift.recurrencePattern),
              to: formatRecurrencePattern(recurrence.recurrence_pattern),
            },
            {
              field: 'Recurrence End Date',
              from: formatRecurrenceDate(currentShift.recurrenceEndDate),
              to: formatRecurrenceDate(recurrence.recurrence_end_date),
            },
          ] as const)
        : ([] as const);

  const changes: readonly NoteShiftChange[] = [
    user,
    ...times,
    status,
    ...recurrencePattern,
  ]
    // Only keep entries that are different
    .filter(({ from, to }) =>
      typeof from !== 'string' && typeof to !== 'string'
        ? from.id !== to.id
        : from !== to
    );

  return changes;
};

/**
 * Returns the exact same {@link newOrUpdatedShift} object,
 * but replaces the current notes with a JSONified string that
 * contains the original notes and a changelog based on the given current shift.
 *
 * This function should be called at the last possible point before the
 * {@link ShiftModifiers} object is sent to the database.
 *
 * @param currentShift - the current shift, to compare the new shift with
 * @param newOrUpdatedUser - the user who is being assigned to this shift
 * @param newOrUpdatedShift - the new or updated shift
 * @returns the {@link newOrUpdatedShift} with a changelog added into its notes
 */
const injectChangelog =
  <T extends CreateOrUpdateShift | ShiftExceptionUpdate>(
    currentShift: FilledShift,
    newOrUpdatedUser: SimpleUser | undefined
  ) =>
  (newOrUpdatedShift: T) => {
    const changes: readonly NoteShiftChange[] = changeLog<T>(
      currentShift,
      newOrUpdatedUser,
      newOrUpdatedShift
    );

    const noteBeingAdded = buildShiftNote(
      newOrUpdatedShift.note?.content,
      format(new Date(currentShift.startTime), 'yyyy-MM-dd'),
      changes
    );

    return {
      ...newOrUpdatedShift,
      note: noteBeingAdded,
    };
  };

/**
 * Add json format wrapper around notes-only changes.
 */
export const jsonifyNotesToCreate = (
  notesToCreate: readonly NoteToCreate[],
  shiftDate: string
): readonly NoteToCreate[] =>
  compact(
    notesToCreate.map((n) => {
      const content = buildShiftNote(
        n.content.content,
        n.content.date ?? shiftDate,
        n.content.metadata?.shift?.changes
      );
      return content === undefined ? undefined : { ...n, content };
    })
  );

/**
 * Takes the current shift and the user for whom the new or updated shifts
 * are for, and applies a changelog to those shifts.
 *
 *  This function should be called at the last possible point before the
 * {@link ShiftModifiers} object is sent to the database.
 *
 * @param shiftModifiers - shift modifiers without changelogs
 * @param currentShift - the shift from which these modifiers were based on
 * @param newOrUpdatedUser - the user for whom the shifts are being modified
 * @returns a {@link ShiftModifiers} object with changelogs added to the create and update shifts
 */
export const addChangelogs = (
  currentShift: FilledShift,
  newOrUpdatedUser: SimpleUser | undefined,
  shiftModifiers: ShiftModifiers
): ShiftModifiers => {
  const injectChangelogs = <
    T extends CreateOrUpdateShift | ShiftExceptionUpdate,
  >(
    shifts: readonly T[]
  ) => shifts.map(injectChangelog(currentShift, newOrUpdatedUser));

  return {
    ...shiftModifiers,
    shiftsToCreate: injectChangelogs(shiftModifiers.shiftsToCreate),
    shiftsToUpdate: injectChangelogs(shiftModifiers.shiftsToUpdate),
    notesToCreate: jsonifyNotesToCreate(
      shiftModifiers.notesToCreate,
      format(new Date(currentShift.startTime), 'yyyy-MM-dd')
    ),
    shiftExceptionUpdates: injectChangelogs(
      shiftModifiers.shiftExceptionUpdates
    ),
  };
};

/**
 * Converts all of the notes within a {@link ShiftModifiers} object into the correct
 * JSON string format.
 *
 * Does not apply a changelog; hence used when shifts are only being created.
 *
 * This function should be called at the last possible point before the
 * {@link ShiftModifiers} object is sent to the database.
 *
 * @param shiftModifiers - shift modifiers with simple string notes
 * @returns - the same {@link ShiftModifiers} object, with the notes from within the
 * create and update arrays converted into the appropriate JSON string format.
 */
export const jsonifyNotesNoChangelog = (
  shiftDate: string,
  shiftModifiers: ShiftModifiers
): ShiftModifiers => {
  const transformNotes = <T extends CreateOrUpdateShift>(
    changes: readonly T[]
  ): readonly T[] =>
    changes.map((s) => ({
      ...s,
      note: buildShiftNote(s.note?.content, shiftDate, undefined),
    }));

  return {
    ...shiftModifiers,
    shiftsToCreate: transformNotes(shiftModifiers.shiftsToCreate),
    shiftsToUpdate: transformNotes(shiftModifiers.shiftsToUpdate),
    notesToCreate: jsonifyNotesToCreate(
      shiftModifiers.notesToCreate,
      shiftDate
    ),
  };
};

const formatDate = (date: Date | null | undefined): string =>
  isNullOrUndefined(date)
    ? defaultEmptyNoteField
    : format(date, shiftDateTimeFormat);

export const formatRecurrencePattern = (
  recurrencePattern: string | undefined | null
): string =>
  (isNullOrUndefined(recurrencePattern) || recurrencePattern === ''
    ? undefined
    : removeApproximateFromRecurrentPatternText(
        RRule.fromString(recurrencePattern).toText()
      )) ?? defaultEmptyNoteField;

export const formatRecurrenceDate = (
  providedDate: Date | string | undefined | null,
  defaultDisplay: string = defaultEmptyNoteField
): string => {
  const date =
    typeof providedDate === 'string' ? providedDate.trim() : providedDate;
  const formatted =
    isNullOrUndefined(date) ||
    date === '9999-12-31T00:00:00.000Z' ||
    date === '9999-12-31T00:00:00+00:00'
      ? defaultDisplay
      : formatDate(new Date(date));
  return formatted;
};

export const fullName = (user: SimpleUser | undefined): string => {
  const trimmed = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
  return trimmed === '' ? defaultEmptyNoteField : trimmed;
};

/**
 * Default note to create when a shift exception is created
 * @param date - the date of the shift exception created
 */
export const shiftExceptionCreatedNote = (date: Date): NoteContent => {
  return {
    content: 'Shift Exception Created',
    date: format(date, 'yyyy-MM-dd'),
    metadata: {
      shift: {
        changes: [
          {
            field: 'Exception Date',
            from: defaultEmptyNoteField,
            to: format(date, 'PPP'),
          },
        ],
      },
    },
  };
};

/**
 * Default note to create when a shift is deleted
 * @param date - the date of the shift deleted
 */
export const shiftDeletedNote = (date: Date): NoteContent => {
  return {
    content: 'Shift Deleted',
    date: format(date, 'yyyy-MM-dd'),
    metadata: {
      shift: {
        changes: [
          {
            field: 'Deleted',
            from: defaultEmptyNoteField,
            to: format(date, 'PPP'),
          },
        ],
      },
    },
  };
};
